/**
 * Intake billing-reality UI: the advisory eligibility banner and the clinician's
 * billing-reality selector.
 *
 * "Reality" is the encounter-type the clinician bills the intake as. It is
 * prefilled from the patient's scheduled eligibility but may diverge. The labels
 * here are UI-only ("CoCM Intake", "Billable Wellness Exam", etc.) — the
 * underlying encounter-type codes and their stored displays are unchanged.
 * @agentic connections
 */

import { Alert } from "@mantine/core";
import { IconClipboardCheck } from "@tabler/icons-react";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { type IntakeBillingFlagCode } from "@benchmark/core/constants";

/**
 * Billing-reality options shown in the complete-intake selector. Values are the
 * three intake encounter-type codes; labels/descriptions are UI-only.
 * @agentic connections
 */
export const INTAKE_BILLING_REALITY_OPTIONS = [
  {
    value: "cocm-intake-interaction",
    label: "CoCM Intake",
    description:
      "Billed as a CoCM Intake. The patient should be enrolled in the CoCM care plan, and all intake minutes count toward CoCM time.",
  },
  {
    value: "clinical-intake-interaction",
    label: "Billable Wellness Exam",
    description:
      "Billed as a standalone wellness exam. Pre-intake and intake minutes do NOT count toward CoCM.",
  },
  {
    value: "unbilled-intake-interaction",
    label: "Non-Billable Wellness Exam",
    description:
      "Not billed. Pre-intake and intake minutes WILL count toward CoCM time.",
  },
] as const;

/**
 * Human-readable eligibility determination shown in the advisory banner.
 * @agentic connections
 */
const ELIGIBILITY_BANNER_MESSAGE: Record<IntakeBillingFlagCode, string> = {
  cocm: "Patient was determined to be CoCM-eligible.",
  wellness: "Patient was determined to be Wellness-eligible.",
  "no-bill":
    "Patient was determined to be ineligible for Wellness or CoCM (No Bill).",
};

/**
 * Advisory banner surfacing the patient's scheduled intake eligibility. Read-only
 * — it informs, but does not constrain, the clinician's billing-reality choice.
 * Shares the blue/gray accent scheme with {@link IntakeBillingRealitySelect} so
 * the two intake-billing surfaces read as one system.
 * @agentic connections
 */
export const IntakeEligibilityBanner = ({
  eligibility,
}: {
  eligibility: IntakeBillingFlagCode | undefined;
}) => {
  const isBillable = eligibility === "cocm" || eligibility === "wellness";
  const message = eligibility
    ? ELIGIBILITY_BANNER_MESSAGE[eligibility]
    : "No intake eligibility was recorded during scheduling.";
  return (
    <Alert
      variant="light"
      color={isBillable ? "blue" : "gray"}
      icon={<IconClipboardCheck size={18} />}
      title="Eligibility (from scheduling)"
      data-testid="intake-eligibility-indicator"
    >
      {message}
    </Alert>
  );
};

/**
 * The clinician's billing-reality selector (prefilled from eligibility).
 * @agentic connections
 */
export const IntakeBillingRealitySelect = ({
  value,
  readOnly = false,
  onChange,
}: {
  value: string;
  readOnly?: boolean;
  onChange: (code: string) => void;
}) => (
  <CardSelect
    data={INTAKE_BILLING_REALITY_OPTIONS.map((o) => ({
      value: o.value,
      label: o.label,
      description: o.description,
    }))}
    value={value || null}
    onChange={(v) => {
      if (readOnly) return;
      const code = Array.isArray(v) ? v[0] : v;
      if (code) onChange(code);
    }}
    columns={3}
    compact
    aria-label="Select how this intake is billed"
  />
);
